import React, { useContext, useEffect, useState } from "react";
import { ColorContext } from "./Context/ColorContext";
import { Button } from "@heroui/react";

const DEFAULT_COLORS = [
  "black", "gray", "white", "red", "orange", "yellow", "green", "blue",
  "indigo", "purple", "pink", "brown"
];

const LOCAL_STORAGE_KEY = "customPalette";

// helper to conditionally join classes
const cx = (...a) => a.filter(Boolean).join(" ");

// swatch size & spacing that scale with viewport height
const SWATCH_SIZE = "w-[clamp(28px,5.5vh,44px)] h-[clamp(28px,5.5vh,44px)]";
const GAP_SIZE = "gap-[clamp(6px,1.1vh,12px)]";

const ColorMenu = () => {
  const { selectedColor, setSelectedColor } = useContext(ColorContext);
  const [colors, setColors] = useState([...DEFAULT_COLORS]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const loaded = JSON.parse(saved);
        if (Array.isArray(loaded) && loaded.length === DEFAULT_COLORS.length) {
          setColors(loaded);
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    const index = colors.findIndex((c) => c === selectedColor);
    if (index !== -1) setSelectedIndex(index);
  }, [selectedColor, colors]);

  const handleColorClick = (color, index) => {
    setSelectedColor(color);
    setSelectedIndex(index);
  };

  const handleColorPickerChange = (e) => {
    const newColor = e.target.value;
    const updated = [...colors];
    updated[selectedIndex] = newColor;
    setColors(updated);
    setSelectedColor(newColor);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };

  // valid hex for the picker (fallback if selectedColor is a named color)
  const pickerValue =
    /^#([0-9A-F]{3}){1,2}$/i.test(selectedColor) ? selectedColor : "#ffffff";

  return (
    <div
      className={cx(
        "fixed right-0 top-1/2 -translate-y-1/2",
        "bg-slate-950/95 text-white rounded-l-2xl z-[1050]",
        "transition-all duration-300 ease-in-out hover:w-32 w-14",
        "py-4 px-2 overflow-hidden",
        // Make sure it ALWAYS fits the screen height and can scroll
        "max-h-[90svh] md:max-h-[92vh] overflow-y-auto overscroll-contain"
      )}
    >
      <div className={cx("flex flex-col items-center", GAP_SIZE)}>
        {colors.map((color, index) => (
          <div key={index} className="relative shrink-0">
            {index === 11 ? (
              <>
                {/* rainbow preview circle */}
                <div
                  className={cx(
                    "rounded-full border border-white/10",
                    SWATCH_SIZE
                  )}
                  style={{
                    background:
                      "conic-gradient(from 180deg at 50% 50%, red, orange, yellow, green, blue, indigo, violet, red)",
                  }}
                />
                <input
                  type="color"
                  onChange={handleColorPickerChange}
                  value={pickerValue}
                  className={cx(
                    "absolute inset-0 opacity-0 cursor-pointer",
                    SWATCH_SIZE
                  )}
                  aria-label="Custom color picker"
                  title="Pick a custom color"
                />
              </>
            ) : (
              <Button
                isIconOnly
                aria-label={color}
                aria-pressed={selectedColor === color}
                onClick={() => handleColorClick(color, index)}
                className={cx(
                  "rounded-full p-0 border border-white/10",
                  SWATCH_SIZE,
                  // highlight selected with a ring (scales nicer than inline border)
                  selectedColor === color &&
                    "ring-2 ring-white ring-offset-2 ring-offset-slate-950"
                )}
                style={{
                  backgroundColor: color,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ColorMenu;
